import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import { presetDetailQuery, presetWorldQuery } from "@/features/presets/queries";
import { OPENING_VIEW, worldSizeAtZoom } from "@/features/presets/world-square";

const MapPane = lazy(() =>
  import("@/components/map/MapPane").then(module => ({ default: module.MapPane })),
);

/**
 * The whole world, centred, at the zoom scenarios open at.
 *
 * Drawing it at the opening zoom rather than whatever fits is what makes the
 * picture usable: borders and map entity names are laid out for the level the
 * page will show them at, so nothing changes size when the live map takes
 * over from the picture.
 */
const WORLD_VIEW = { ...OPENING_VIEW, center: [0, 0] as [number, number] };

export const Route = createFileRoute("/presets/$slug/still")({
  component: PresetStillPage,
});

/**
 * A scenario's world, square, and nothing else on the screen.
 *
 * This is what `bun run capture:map-previews` photographs, and the picture it
 * files is what the scenario's page shows in place of the live map. Taking it
 * from the map itself, at the scale the page lays it out, is what lets the
 * page place it on the pixels the live map will use.
 */
function PresetStillPage() {
  const { slug } = Route.useParams();
  const [revealed, setRevealed] = useState(false);
  // The box is the world: the engine is asked how large it draws it here, so
  // the picture is the square itself with no margin and nothing cropped.
  const side = worldSizeAtZoom(WORLD_VIEW.zoom);
  const { data: detail } = useQuery(presetDetailQuery(slug));
  const { data: world } = useQuery({
    ...presetWorldQuery(detail?.presetVersionId ?? "", detail?.world.url ?? ""),
    enabled: detail !== undefined,
  });

  return (
    <div
      data-map-still={revealed ? "revealed" : "loading"}
      // Over the app rather than inside it: the picture is the map's own
      // square, so nothing may pad it, round it, or crop it.
      className="fixed top-0 left-0 z-50 bg-black"
      style={{ width: side, height: side }}
    >
      {world && (
        <Suspense fallback={null}>
          <MapPane
            map={world.map}
            center={WORLD_VIEW.center}
            zoom={WORLD_VIEW.zoom}
            onReveal={() => setRevealed(true)}
          />
        </Suspense>
      )}
    </div>
  );
}
